// Login.js
import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import './Login.css';
import AuthContext from "../context/AuthProvider";
import api, { updateGpsCache, startGeoPingIfCheckedIn, resetLogoutGuard } from "./api";
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import InstallPrompt from './InstallPrompt';
import { shouldPersistAuth } from '../utils/deviceDetect';

/* ─── Role-button configs ────────────────────────────────────────────────── */
const ROLE_BTNS = [
  {
    role: 'saadhak',
    color: '#6ee7b7',      // green-300
    bd: 'rgba(52,211,153,0.45)',
    bg: 'rgba(16,185,129,0.10)',
    icoBg: 'rgba(16,185,129,0.18)',
    icoBd: 'rgba(52,211,153,0.40)',
    img: '/SaadhakLogin-img.png?v=2',
    title: 'Saadhak',
    sub: 'Begin your healing transformation',
  },
  {
    role: 'therapist',
    color: '#93c5fd',      // blue-300
    bd: 'rgba(96,165,250,0.45)',
    bg: 'rgba(59,130,246,0.10)',
    icoBg: 'rgba(59,130,246,0.18)',
    icoBd: 'rgba(96,165,250,0.40)',
    img: '/therapistLogin.png',
    title: 'Therapist',
    sub: 'Empower healing, transform lives',
  },
];

/* ─── Geolocation helper ─────────────────────────────────────────────────── */
function getGeoCoords() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location access is not supported by this browser. Please use Chrome or Safari and try again.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ userLat: pos.coords.latitude, userLon: pos.coords.longitude }),
      (err) => {
        if (err.code === 1) {
          reject(new Error('Location access denied. Tap the lock icon in your address bar, set Location to "Allow", then reload the page.'));
        } else {
          reject(new Error('Unable to determine your location. Please enable GPS and try again.'));
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

/* ─── Main component ─────────────────────────────────────────────────────── */
const Login = () => {
  const { auth, setAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  // 'landing' | 'therapist' | 'saadhak'
  const [loginMode, setLoginMode] = useState('landing');

  // Therapist fields
  const [username,     setUsername]     = useState('');
  const [password,     setPassword]     = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  // Staff OTP fields
  const [staffOtpStep,    setStaffOtpStep]    = useState(false); // true when awaiting OTP
  const [staffOtp,        setStaffOtp]        = useState('');
  const [staffOtpLoading, setStaffOtpLoading] = useState(false);
  const [staffResend,     setStaffResend]     = useState(0);
  const [staffGeo,        setStaffGeo]        = useState(null); // saved GPS for resend

  // Saadhak fields
  const [patientCard, setPatientCard] = useState('');
  const [otpStep,     setOtpStep]     = useState(1);
  const [saadhakMode, setSaadhakMode] = useState('');
  const [otp,         setOtp]         = useState('');
  const [phone,       setPhone]       = useState('');
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Shared
  const [errorMessage,  setErrorMessage]  = useState('');
  const [loginLoading,  setLoginLoading]  = useState(false);

  // Location permission state: 'checking' | 'granted' | 'prompt' | 'denied'
  const [locPerm, setLocPerm] = useState('checking');

  const usernameRef    = useRef(null);
  const otpInputRef    = useRef(null);
  const staffOtpRef    = useRef(null);
  const timerRef       = useRef(null);
  const staffTimerRef  = useRef(null);

  // ── Redirect if already authenticated (PWA reopen / session restore) ──────
  // When mobile PWA reopens, iOS restores last URL (often /login).
  // AuthProvider already restored auth from localStorage, so redirect immediately.
  useEffect(() => {
    if (auth?.roles && auth?.token) {
      const roles = auth.roles || [];
      if (roles.includes('saadhak') || roles.includes('saadhakdashboard')) {
        navigate('/SaadhakDashboard', { replace: true });
      } else if (auth.limitedRemoteAccess && !auth.allowedOutside) {
        navigate('/TherapistDashboard', { replace: true });
      } else if (roles.includes('therapist')) {
        navigate('/TherapistDashboard', { replace: true });
      } else {
        navigate('/ListPatients', { replace: true });
      }
    }
  }, [auth, navigate]);

  // On login page mount: wipe any stale session tokens and reset logout guard.
  // This prevents NotificationContext / SessionTabStrip from firing API calls
  // with expired tokens → 401 → alert storm while the user is on the login screen.
  // Skip if user has valid auth (session restore in progress — AuthProvider will handle).
  useEffect(() => {
    const hasValidAuth = localStorage.getItem('auth');
    if (!hasValidAuth) {
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('lu');
    }
    resetLogoutGuard();
  }, []);

  // Check location permission on therapist login screen
  useEffect(() => {
    if (loginMode !== 'therapist') { setLocPerm('checking'); return; }
    let cancelled = false;
    (async () => {
      try {
        if (navigator.permissions) {
          const perm = await navigator.permissions.query({ name: 'geolocation' });
          if (!cancelled) setLocPerm(perm.state); // 'granted' | 'prompt' | 'denied'
          perm.onchange = () => { if (!cancelled) setLocPerm(perm.state); };
        } else {
          // Permissions API not supported (older iOS) — try a silent probe
          navigator.geolocation.getCurrentPosition(
            () => { if (!cancelled) setLocPerm('granted'); },
            (e) => { if (!cancelled) setLocPerm(e.code === 1 ? 'denied' : 'prompt'); },
            { timeout: 3000, maximumAge: 60000 }
          );
        }
      } catch (_) { if (!cancelled) setLocPerm('prompt'); }
    })();
    return () => { cancelled = true; };
  }, [loginMode]);

  useEffect(() => {
    setErrorMessage('');
    setOtpStep(1);
    setSaadhakMode('');
    setOtp('');
    setPhone('');
    setResendTimer(0);
    clearInterval(timerRef.current);
    setStaffOtpStep(false); setStaffOtp(''); setStaffResend(0); setStaffGeo(null);
    clearInterval(staffTimerRef.current);
    if (usernameRef.current) usernameRef.current.focus();
  }, [loginMode]);

  useEffect(() => {
    if (otpStep === 2 && otpInputRef.current) otpInputRef.current.focus();
  }, [otpStep]);

  useEffect(() => {
    if (staffOtpStep && staffOtpRef.current) staffOtpRef.current.focus();
  }, [staffOtpStep]);

  function startResendTimer() {
    setResendTimer(60);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(t => {
        if (t <= 1) { clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  }

  function startStaffResendTimer() {
    setStaffResend(60);
    clearInterval(staffTimerRef.current);
    staffTimerRef.current = setInterval(() => {
      setStaffResend(t => {
        if (t <= 1) { clearInterval(staffTimerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  }

  function finishSaadhakLogin(data) {
    const { token, name, patientCard: card } = data.data;
    const roles    = ['saadhak', 'saadhakdashboard'];
    // Use patientCard as username — matches JWT (patientCard), SSE, and push subscriptions
    const authData = { username: card?.toLowerCase(), roles, token, patientCard: card, designation: 'Saadhak', displayName: name, branch: data.data.profile?.branch || '' };
    setAuth(authData);
    if (shouldPersistAuth(roles, authData)) localStorage.setItem('auth', JSON.stringify(authData));
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('lu', card);
    setLoginSuccess(true);
    navigate('/SaadhakDashboard', { replace: true, state: { profile: data.data.profile } });
  }

  function finishStaffLogin(data) {
    const { token, username: user, roles, empId, permissions, screens, designation, branch, allowedOutside, limitedRemoteAccess, remoteExpenseAccess, remoteTaskAccess, mustChangePassword } = data.data;
    const allowedScreens = screens || [];
    setAuth({ username: user?.toLowerCase(), roles, token, empId, allowedScreens, permissions: permissions || [], designation: designation || '', branch: branch || '', allowedOutside: !!allowedOutside, limitedRemoteAccess: !!limitedRemoteAccess, remoteExpenseAccess: !!remoteExpenseAccess, remoteTaskAccess: !!remoteTaskAccess });
    sessionStorage.setItem('token', token);
    sessionStorage.setItem('lu', user);
    setUsername('');
    setPassword('');
    setLoginSuccess(true);
    startGeoPingIfCheckedIn();

    if (mustChangePassword) {
      navigate('/ChangePassword', { state: { forced: true } });
      return;
    }
    const userRoles = roles || [];
    if (limitedRemoteAccess && !allowedOutside) {
      navigate('/TherapistDashboard');
    } else if (userRoles.includes('therapist')) {
      navigate('/TherapistDashboard');
    } else {
      navigate('/ListPatients');
    }
  }

  const handleStaffVerifyOTP = async (event) => {
    event.preventDefault();
    if (!staffOtp || staffOtp.length !== 4) { setErrorMessage('Enter the 4-digit OTP.'); return; }
    setStaffOtpLoading(true);
    setErrorMessage('');
    try {
      const response = await api.post(process.env.REACT_APP_URL + '/verify-login-otp', { username, otp: staffOtp.trim() });
      finishStaffLogin(response.data);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setStaffOtpLoading(false);
    }
  };

  const handleStaffResendOTP = async () => {
    setStaffOtpLoading(true);
    setErrorMessage('');
    try {
      const geo = staffGeo || {};
      const response = await api.post(process.env.REACT_APP_URL + '/login', {
        username, password, userLat: geo.userLat, userLon: geo.userLon
      });
      if (response.data.requiresOtp) {
        startStaffResendTimer();
        if (response.data.devOtp) setErrorMessage(`[DEV] OTP: ${response.data.devOtp}`);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Failed to resend OTP.');
    } finally {
      setStaffOtpLoading(false);
    }
  };

  const handleTherapistLogin = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    setErrorMessage('');
    try {
      // Try to get GPS — if it fails (remote user, denied permission), send null and let backend decide
      let userLat = null, userLon = null;
      try {
        const coords = await getGeoCoords();
        userLat = coords.userLat;
        userLon = coords.userLon;
        updateGpsCache(userLat, userLon);
      } catch (_) { /* remote users may not have GPS — backend handles allowedOutside/limitedRemoteAccess */ }

      const response = await api.post(process.env.REACT_APP_URL + '/login', { username, password, userLat, userLon });
      const data = response.data;

      // ── Staff OTP required ──
      if (data.requiresOtp) {
        setStaffOtpStep(true);
        setStaffGeo({ userLat, userLon });
        startStaffResendTimer();
        if (data.devOtp) setErrorMessage(`[DEV] OTP: ${data.devOtp}`);
        return;
      }

      finishStaffLogin(data);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || err.message || 'Error during login');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleCardSubmit = async (event) => {
    event.preventDefault();
    if (!patientCard.trim()) { setErrorMessage('Please enter your Patient Card ID.'); return; }
    setOtpLoading(true);
    setErrorMessage('');
    try {
      const res = await api.post(process.env.REACT_APP_URL + '/saadhak-otp/request', { patientCard: patientCard.trim() });
      const mode = res.data?.mode || 'phone';
      setSaadhakMode(mode);
      setOtpStep(2);
      if (mode === 'otp') {
        startResendTimer();
        if (res.data?.devOtp) setErrorMessage(`[DEV] OTP: ${res.data.devOtp}`);
      }
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Patient not found. Check your Card ID.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOTP = async (event) => {
    event.preventDefault();
    if (!otp || otp.length !== 4) { setErrorMessage('Enter the 4-digit OTP.'); return; }
    setOtpLoading(true);
    setErrorMessage('');
    try {
      const response = await api.post(process.env.REACT_APP_URL + '/saadhak-otp/verify', { patientCard: patientCard.trim(), otp: otp.trim() });
      finishSaadhakLogin(response.data);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyPhone = async (event) => {
    event.preventDefault();
    if (!phone || phone.replace(/\D/g, '').length < 10) {
      setErrorMessage('Enter your 10-digit registered phone number.');
      return;
    }
    setOtpLoading(true);
    setErrorMessage('');
    try {
      const response = await api.post(process.env.REACT_APP_URL + '/saadhak-otp/verify', { patientCard: patientCard.trim(), phone: phone.trim() });
      finishSaadhakLogin(response.data);
    } catch (err) {
      setErrorMessage(err.response?.data?.message || 'Phone number does not match our records.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── Shared input-icon style ──
  const icoSt = {
    position: 'absolute', left: 14, top: '50%',
    transform: 'translateY(-50%)',
    color: 'rgba(100,150,255,0.5)', fontSize: 15,
    zIndex: 1, pointerEvents: 'none',
  };

  // ── Form-panel metadata based on current mode ──
  const formColor = loginMode === 'therapist' ? '#93c5fd' : '#6ee7b7';
  const formBg    = loginMode === 'therapist' ? 'rgba(59,130,246,0.15)' : 'rgba(16,185,129,0.15)';
  const formImg   = loginMode === 'therapist' ? '/therapistLogin.png' : '/SaadhakLogin-img.png?v=2';
  const formTitle = loginMode === 'therapist' ? 'Therapist Login' : 'Saadhak Login';
  const formSub   = loginMode === 'therapist' ? 'Empower healing, transform lives' : 'Begin your healing transformation';
  const submitBg  = loginMode === 'therapist'
    ? 'linear-gradient(135deg,#2563eb,#3b82f6)'
    : 'linear-gradient(135deg,#059669,#10b981)';

  // Don't render login UI if already authenticated — prevents flash/flicker during redirect
  if (auth?.roles && auth?.token) {
    return <div style={{ minHeight: '100vh', background: '#040e0a' }} />;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#040e0a', display: 'flex', flexDirection: 'column', alignItems: 'center', overflowX: 'hidden' }}>
      <InstallPrompt />

      {/* ══════════════════════════════════════════════════
          LANDING — hero image + role buttons
         ══════════════════════════════════════════════════ */}
      {loginMode === 'landing' && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          width: '100%', maxWidth: 480, boxSizing: 'border-box', minHeight: '100vh',
          position: 'relative',
        }}>
          {/* Hero image — logo, globe, circuit lines (crop before image's built-in buttons) */}
          <div style={{
            width: '100%', position: 'relative',
            paddingTop: '85%',    /* crop before the image's built-in saadhak card */
            backgroundImage: 'url(/myc-hero.jpeg)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'top center',
            flexShrink: 0,
          }} />

          {/* Role buttons — positioned below the hero */}
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 14, width: '100%',
            padding: '24px 20px 40px',
            position: 'relative', zIndex: 2,
          }}>
            {ROLE_BTNS.map(btn => (
              <button key={btn.role}
                className="myc-role-btn"
                onClick={() => setLoginMode(btn.role)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16,
                  background: btn.bg,
                  border: `1.5px solid ${btn.bd}`,
                  backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                  borderRadius: 18, padding: '15px 18px 15px 14px',
                  width: '100%', cursor: 'pointer',
                  textAlign: 'left', WebkitTapHighlightColor: 'transparent',
                }}>
                <div style={{
                  width: 52, height: 52, borderRadius: '50%',
                  background: btn.icoBg, border: `1.5px solid ${btn.icoBd}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  overflow: 'hidden',
                }}>
                  <img src={btn.img} alt={btn.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: btn.color, lineHeight: 1.25 }}>{btn.title}</div>
                  <div style={{ fontSize: 12, color: 'rgba(180,220,200,0.55)', marginTop: 3, lineHeight: 1.4 }}>{btn.sub}</div>
                </div>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  border: `1.5px solid ${btn.bd}`, display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <i className="fas fa-arrow-right" style={{ fontSize: 14, color: btn.color }} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════
          FORM SCREEN — Therapist or Saadhak
         ══════════════════════════════════════════════════ */}
      {loginMode !== 'landing' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: 440, padding: '0 20px 44px', boxSizing: 'border-box', minHeight: '100vh' }}>

          {/* Back nav */}
          <div style={{ width: '100%', padding: '16px 0 4px' }}>
            <button
              onClick={() => { setLoginMode('landing'); setUsername(''); setPassword(''); }}
              style={{ background: 'none', border: 'none', color: 'rgba(148,163,184,0.8)', fontSize: 14, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 0', WebkitTapHighlightColor: 'transparent' }}>
              <i className="fas fa-chevron-left" style={{ fontSize: 11 }} /> Home
            </button>
          </div>

          {/* Mini logo */}
          <div style={{ textAlign: 'center', margin: '18px 0 28px' }}>
            <div style={{ fontSize: 42, fontWeight: 900, letterSpacing: 6, background: 'linear-gradient(135deg,#34d399,#a3e635,#d4a017)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1 }}>
              MYC
            </div>
            <div style={{ fontSize: 10, color: 'rgba(160,200,180,0.5)', letterSpacing: 2.5, marginTop: 5, textTransform: 'uppercase' }}>
              Medical Yoga Therapy Centre
            </div>
          </div>

          {/* Form card */}
          <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(52,211,153,0.18)', borderRadius: 22, padding: '28px 28px 32px', width: '100%', boxSizing: 'border-box' }}>

            {/* Card header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 13, marginBottom: 22, paddingBottom: 20, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: formBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                <img src={formImg} alt={formTitle} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
              </div>
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.2 }}>{formTitle}</div>
                <div style={{ fontSize: 12, color: 'rgba(148,163,184,0.58)', marginTop: 2 }}>{formSub}</div>
              </div>
            </div>

            {/* Error message */}
            <p ref={usernameRef} className={errorMessage ? 'errmsg' : 'offscreen'} aria-live="assertive">
              {errorMessage}
            </p>

            {/* ── LOCATION DENIED BANNER ── */}
            {loginMode === 'therapist' && locPerm === 'denied' && (
              <div style={{
                background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.35)',
                borderRadius: 12, padding: '14px 16px', marginBottom: 16, fontSize: 13, lineHeight: 1.55,
                color: '#fbbf24',
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Location Access Required</div>
                <div style={{ color: 'rgba(253,224,71,0.7)', fontSize: 12 }}>
                  Location was previously denied. To enable it:
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    <li>Tap the lock/info icon in the address bar</li>
                    <li>Set Location to "Allow"</li>
                    <li>Reload this page</li>
                  </ul>
                </div>
              </div>
            )}

            {/* ── THERAPIST FORM (password step) ── */}
            {loginMode === 'therapist' && !staffOtpStep && (
              <form onSubmit={handleTherapistLogin}>
                <div style={{ position: 'relative', marginBottom: 14 }}>
                  <i className="fas fa-user" style={icoSt} />
                  <input type="text" className="myc-input" name="username" autoComplete="off"
                    placeholder="Username / Email" value={username}
                    onChange={e => setUsername(e.target.value)} required />
                </div>
                <div style={{ position: 'relative', marginBottom: 4 }}>
                  <i className="fas fa-lock" style={icoSt} />
                  <input type={showPassword ? 'text' : 'password'} className="myc-input" name="password"
                    placeholder="Password" value={password}
                    onChange={e => setPassword(e.target.value)}
                    style={{ paddingRight: 42 }} />
                  {showPassword
                    ? <FaEyeSlash onClick={() => setShowPassword(false)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(100,150,255,0.5)', fontSize: 16, zIndex: 2, cursor: 'pointer' }} />
                    : <FaEye onClick={() => setShowPassword(true)} style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'rgba(100,150,255,0.5)', fontSize: 16, zIndex: 2, cursor: 'pointer' }} />
                  }
                </div>
                <button type="submit" className="myc-submit-btn" style={{ marginTop: 22, background: submitBg }} disabled={loginLoading}>
                  {loginLoading ? 'Locating…' : 'Log In Now'} &nbsp;<i className="fas fa-arrow-right" />
                </button>
              </form>
            )}

            {/* ── THERAPIST OTP STEP ── */}
            {loginMode === 'therapist' && staffOtpStep && (
              <form onSubmit={handleStaffVerifyOTP}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>User:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginLeft: 6 }}>{username}</span>
                  <button type="button"
                    onClick={() => { setStaffOtpStep(false); setStaffOtp(''); setErrorMessage(''); setStaffResend(0); clearInterval(staffTimerRef.current); }}
                    style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(148,163,184,0.6)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Back
                  </button>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(148,163,184,0.55)', marginBottom: 14, lineHeight: 1.5 }}>
                  An OTP has been sent to your registered WhatsApp number.
                </div>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <i className="fas fa-key" style={icoSt} />
                  <input ref={staffOtpRef} type="number" className="myc-input" name="staffOtp"
                    placeholder="4-digit OTP" value={staffOtp}
                    onChange={e => setStaffOtp(e.target.value.slice(0, 4))}
                    inputMode="numeric" required />
                </div>
                <div style={{ fontSize: 12, color: 'rgba(148,163,184,0.55)', marginBottom: 20 }}>
                  {staffResend > 0
                    ? `Resend OTP in ${staffResend}s`
                    : (
                      <button type="button" onClick={handleStaffResendOTP}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#93c5fd', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
                        Resend OTP
                      </button>
                    )
                  }
                </div>
                <button type="submit" className="myc-submit-btn" style={{ background: submitBg }} disabled={staffOtpLoading}>
                  {staffOtpLoading ? 'Verifying…' : 'Verify & Log In'} &nbsp;<i className="fas fa-arrow-right" />
                </button>
              </form>
            )}

            {/* ── SAADHAK Step 1: Card ID ── */}
            {loginMode === 'saadhak' && otpStep === 1 && (
              <form onSubmit={handleCardSubmit}>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <i className="fas fa-id-card" style={icoSt} />
                  <input type="text" className="myc-input" name="patientCard" autoComplete="off"
                    placeholder="Patient Card ID (e.g. MYC001)" value={patientCard}
                    onChange={e => setPatientCard(e.target.value)} required />
                </div>
                <div style={{ fontSize: 11, color: 'rgba(180,200,255,0.42)', marginBottom: 20, lineHeight: 1.55 }}>
                  Login with your registered card ID and phone / OTP.
                </div>
                <button type="submit" className="myc-submit-btn" style={{ background: submitBg }} disabled={otpLoading}>
                  {otpLoading ? 'Checking…' : 'Continue'} &nbsp;<i className="fas fa-arrow-right" />
                </button>
              </form>
            )}

            {/* ── SAADHAK Step 2a: OTP ── */}
            {loginMode === 'saadhak' && otpStep === 2 && saadhakMode === 'otp' && (
              <form onSubmit={handleVerifyOTP}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>Card:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginLeft: 6 }}>{patientCard}</span>
                  <button type="button"
                    onClick={() => { setOtpStep(1); setSaadhakMode(''); setOtp(''); setErrorMessage(''); }}
                    style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(148,163,184,0.6)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Change
                  </button>
                </div>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <i className="fas fa-key" style={icoSt} />
                  <input ref={otpInputRef} type="number" className="myc-input" name="otp"
                    placeholder="4-digit OTP" value={otp}
                    onChange={e => setOtp(e.target.value.slice(0, 4))}
                    inputMode="numeric" required />
                </div>
                <div style={{ fontSize: 12, color: 'rgba(148,163,184,0.55)', marginBottom: 20 }}>
                  {resendTimer > 0
                    ? `Resend OTP in ${resendTimer}s`
                    : (
                      <button type="button"
                        onClick={async () => {
                          setOtpLoading(true); setErrorMessage('');
                          try {
                            const r = await api.post(process.env.REACT_APP_URL + '/saadhak-otp/request', { patientCard: patientCard.trim() });
                            startResendTimer();
                            if (r.data?.devOtp) setErrorMessage(`[DEV] OTP: ${r.data.devOtp}`);
                          } catch (e) { setErrorMessage(e.response?.data?.message || 'Failed to resend'); }
                          finally { setOtpLoading(false); }
                        }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c084fc', fontSize: 12, padding: 0, textDecoration: 'underline' }}>
                        Resend OTP
                      </button>
                    )
                  }
                </div>
                <button type="submit" className="myc-submit-btn" style={{ background: submitBg }} disabled={otpLoading}>
                  {otpLoading ? 'Verifying…' : 'Enter Portal 🙏'}
                </button>
              </form>
            )}

            {/* ── SAADHAK Step 2b: Phone ── */}
            {loginMode === 'saadhak' && otpStep === 2 && saadhakMode === 'phone' && (
              <form onSubmit={handleVerifyPhone}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                  <span style={{ fontSize: 11, color: 'rgba(148,163,184,0.6)' }}>Card:</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginLeft: 6 }}>{patientCard}</span>
                  <button type="button"
                    onClick={() => { setOtpStep(1); setSaadhakMode(''); setPhone(''); setErrorMessage(''); }}
                    style={{ marginLeft: 'auto', fontSize: 11, color: 'rgba(148,163,184,0.6)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Change
                  </button>
                </div>
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <i className="fas fa-phone" style={icoSt} />
                  <input ref={otpInputRef} type="tel" className="myc-input" name="phone"
                    placeholder="Registered Phone Number" value={phone}
                    onChange={e => setPhone(e.target.value)}
                    maxLength={10} inputMode="numeric" required />
                </div>
                <button type="submit" className="myc-submit-btn" style={{ marginTop: 8, background: submitBg }} disabled={otpLoading}>
                  {otpLoading ? 'Verifying…' : 'Enter Portal 🙏'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default Login;
